import { Sun, Moon, LogOut, Sparkles } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useTheme } from "@/lib/theme";
import { useAuth, useLogout } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { PropertySwitcher } from "./PropertySwitcher";

function initials(nameOrEmail: string): string {
  const parts = nameOrEmail.replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("");
  return letters || "?";
}

export default function AppTopbar({ onOpenChat }: { onOpenChat?: () => void }) {
  const { theme, toggle } = useTheme();
  const { user } = useAuth();
  const logout = useLogout();
  const nav = useNavigate();

  async function onLogout() {
    try {
      await logout.mutateAsync();
    } finally {
      nav("/login", { replace: true });
    }
  }

  return (
    <header className="flex h-16 shrink-0 items-center justify-between border-b border-border bg-background/80 px-8 backdrop-blur">
      <div className="flex items-center gap-3">
        <PropertySwitcher />
      </div>

      <div className="flex items-center gap-2">
        {onOpenChat && (
          <button
            type="button"
            onClick={onOpenChat}
            aria-label="Open Auriel chat"
            title="Ask Auriel"
            className="flex h-9 items-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-2.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
          >
            <Sparkles className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Ask Auriel</span>
          </button>
        )}

        <button
          type="button"
          onClick={toggle}
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        <div className="ml-2 hidden text-right text-xs text-muted-foreground md:block">
          <div className="font-medium text-foreground">
            {user?.full_name ?? user?.email ?? "—"}
          </div>
          <div>Tenant: {user?.tenant_id ?? "—"}</div>
        </div>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              aria-label="Account menu"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/70 text-sm font-medium text-primary-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {user ? initials(user.full_name ?? user.email) : "?"}
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={8}
              className="z-50 w-56 rounded-md border border-border bg-card p-1 text-sm shadow-md"
            >
              {user && (
                <div className="px-3 py-2">
                  <div className="font-medium">{user.full_name ?? user.email}</div>
                  <div className="text-xs text-muted-foreground">{user.email}</div>
                </div>
              )}
              <DropdownMenu.Separator className="my-1 h-px bg-border" />
              <DropdownMenu.Item
                onSelect={() => onLogout()}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm outline-none transition-colors data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
